"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { getIndexPageUrl, getTopStockPageUrl } from "@/app/utils/stock-url";
import { DirectPaths } from "@/config/routes";

export const SEO_TABS = [
    "Share Market",
    // "F&O",
    // "IPO",
    "Indicies",
    "Top Stocks",
    "Global Stocks",
    "Calculators",
    "Knowledge Centre",
    // "Blogs",
    // "More",
] as const;

/** Hidden SEO tabs — commented out of SEO_TABS but retained for content handlers. */
type HiddenSeoTab = "F&O" | "IPO" | "Blogs" | "More";

export type FooterSeoTab = (typeof SEO_TABS)[number] | HiddenSeoTab;

const SHARE_MARKET_TODAY_LINKS = [
    "Top Gainers",
    "Top Losers",
    "Most Traded Stocks",
    "52 Week High",
    "Stock Market Calendar",
    "Intraday Stocks",
    "52 Week Low",
] as const;

const ETF_LINKS = [
    // "Global",
    "Debt",
    "Gold",
    "Commodity",
    "Equity",
    "Silver",
] as const;

const FUTURES_LINKS = [
    "Infosys Futures",
    "Bajaj Finance",
    "Reliance Futures",
    "HDFC Bank",
    "ICICI Bank",
    "TCS",
    "SBI",
    "Axis Bank",
    "Tata Motors",
    "Adani Enterprises",
    "ICICI Bank",
    "Infosys",
] as const;

const OPTIONS_LINKS = [
    "Infosys Futures",
    "Bajaj Finance",
    "Reliance Futures",
    "HDFC Bank",
    "ICICI Bank",
    "Adani Enterprises",
    "SBI",
    "Axis Bank",
    "TCS",
    "Infosys",
] as const;

/** IPO tab columns — Figma desktop 4834:87113 */
const IPO_COLUMNS = [
    ["Open IPO", "Millworks IPO Allotment Status"],
    ["Upcoming IPO"],
    ["Closed IPO"],
    ["Zepto IPO"],
    ["SBI IPO Allotment Status"],
] as const;

/** IPO tab mobile — Figma 4834:88006 (2 columns, gap 24) */
const IPO_MOBILE_COLUMNS = [
    ["Open IPO", "Upcoming IPO", "SBI IPO Allotment Status"],
    ["Closed IPO", "Zepto IPO", "Millworks IPO Allotment Status"],
] as const;

const INDIAN_INDICES_LINKS = [
    "Nifty 50",
    "Nifty Bank",
    "BSE Sensex",
    // "India VIX",
    "Nifty Next 50",
    "Nifty Metal",
    "Nifty 100",
    // "Nifty Fin Services",
    // "Nifty Reality",
    "Nifty Pharma",
] as const;

const GLOBAL_INDICES_LINKS = [
    "Nasdaq",
    "Dow Jones Index",
    "Dow Jones Futures",
    "KOSPI Index",
    "DAX Index",
    "CAC Index",
    "Hang Seng Index",
    "Gift Nifty",
] as const;

/** Top Stocks tab — Figma 4834:86385 (collapsed). `\n` = designed line break. */
const TOP_STOCKS_COLUMNS = [
    [
        "Ashok Leyland\nShare Price",
        "CDSL Share Price",
        "Adani Wilmar Share Price",
        "Canara Bank Share Price",
        "COAL India Share Price",
    ],
    [
        "Adani Ports Share Price",
        "HDFC Bank Share Price",
        "Eicher Motors Share Price",
        "Bajaj Finance Share Price",
        "Tata Consultancy Services Share Price",
    ],
    [
        "HFCL Share Price",
        "Dmart Share Price",
        "HCL Technologies\nShare Price",
        "HUL Share Price",
        "Infosys Share Price",
    ],
    [
        "Eternal (Zomato)\nShare Price",
        "ICICI Bank Share Price",
        "ITC Share Price",
        "JSW Steel Share Price",
        "Hindalco Industries\nShare Price",
    ],
    [
        "IRCTC Share Price",
        "NTPC Share Price",
        "Larsen & Toubro\nShare Price",
        "Power Grid Corporation Share Price",
        "Cipla Share Price",
    ],
] as const;

/** Extra rows revealed by Show More (no expanded frame in Figma). */
const TOP_STOCKS_MORE_COLUMNS = [
    [
        "Reliance Industries Share Price",
        "SBI Share Price",
        "Axis Bank Share Price",
        "Maruti Suzuki Share Price",
        "Tata Motors Share Price",
    ],
    [
        "Wipro Share Price",
        "Bharti Airtel Share Price",
        "Sun Pharma Share Price",
        "Asian Paints Share Price",
        "Titan Share Price",
    ],
    [
        "UltraTech Cement Share Price",
        "Nestle India Share Price",
        "Mahindra & Mahindra Share Price",
        "Tech Mahindra Share Price",
        "IndusInd Bank Share Price",
    ],
    [
        "Bajaj Auto Share Price",
        "Dr Reddy's Share Price",
        "Adani Enterprises Share Price",
        "ONGC Share Price",
        "BPCL Share Price",
    ],
    [
        "Tata Steel Share Price",
        "Vedanta Share Price",
        "Pidilite Share Price",
        "Godrej Consumer Share Price",
        "Divi's Lab Share Price",
    ],
] as const;

const TOP_US_STOCKS_LINKS = [
    {
        label: "Apple Inc",
        path: "/us-stocks/AAPL/apple-inc",
    },
    {
        label: "Alphabet Inc",
        path: "/us-stocks/GOOGL/alphabet-inc",
    },
    {
        label: "Microsoft Corp",
        path: "/us-stocks/MSFT/microsoft-corp",
    },
    {
        label: "Amazon.com Inc",
        path: "/us-stocks/AMZN/amazon.com-inc",
    },
    {
        label: "Space Exploration Technologies Corp",
        path: "/us-stocks/SPCX/space-exploration-technologies-corp",
    },
] as const;

const US_ETFS_LINKS = [
    {
        label: "iShares MSCI South Korea ETF",
        path: "/us-etfs/EWY/ishares-msci-south-korea-etf",
    },
    {
        label: "iShares China Large-Cap ETF",
        path: "/us-etfs/FXI/ishares-china-large-cap-etf",
    },
    {
        label: "VanEck Gold Miners ETF",
        path: "/us-etfs/GDX/vaneck-gold-miners-etf",
    },
    {
        label: "iShares Core S&P 500 ETF",
        path: "/us-etfs/IVV/ishares-core-s&p-500-etf",
    },
    {
        label: "iShares Russell 2000 ETF",
        path: "/us-etfs/IWM/ishares-russell-2000-etf",
    },
] as const;

/** Calculators tab — Figma 4834:86939 */
const CALCULATOR_COLUMNS = [
    ["SIP Calculator", "Income Tax Calculator", "DCF Calculator"],
    ["Brokerage Calculator", "MF Calculator"],
    ["Lumpsum Calculator", "ROI Calculator"],
    ["Margin Calculator", "SWP Calculator"],
    ["FD Calculator", "MTF Calculator"],
] as const;

/** Path suffixes under ICM_KNOWLEDGE_CENTRE (no absolute URLs). */
const KNOWLEDGE_CENTER_SLUGS: Record<string, string> = {
    "What is a Demat Account Holding Statement?":
        "demat-account/what-is-demat-account-holding-statement/",
    "How to check your demat account details using your PAN Card?":
        "demat-account/how-to-check-your-demat-account-details-using-your-pan-card/",
    "How to Open a Demat Account for NRI?": "demat-account/how-to-open-nri-demat-account/",
    "How to Calculate Stop Loss in\nIntraday Trading?":
        "intraday-trading/how-to-calculate-stop-loss-in-intraday-trading/",
    "How to Calculate Stop Loss in Intraday Trading?":
        "intraday-trading/how-to-calculate-stop-loss-in-intraday-trading/",
    "What is Intraday Trading?": "intraday-trading/what-is-intraday-trading/",
    "What is the IPO Listing Time in India?": "ipo/what-is-the-ipo-listing-time-in-india/",
    "Types of Investors in an IPO": "ipo/types-of-investors-in-an-ipo/",
    "How to Apply for IPO in HNI Category?": "ipo/how-to-apply-for-ipo-in-hni-category/",
};

function knowledgeCentreHref(label: string): string | null {
    const slug = KNOWLEDGE_CENTER_SLUGS[label];
    if (!slug) return null;
    const base = (process.env.ICM_KNOWLEDGE_CENTRE || "").replace(/\/$/, "");
    if (!base) return null;
    return `${base}/${slug}`;
}

function calculatorHref(label: string): string | null {
    if (label === "FD Calculator") return DirectPaths.CALCULATOR;
    return null;
}

/** Knowledge Centre tab — Figma 4834:86745. `\n` = designed line break. */
const KNOWLEDGE_CENTER_SECTIONS = [
    {
        title: "DEMAT ACCOUNT",
        links: [
            "What is a Demat Account Holding Statement?",
            "How to check your demat account details using your PAN Card?",
            "How to Open a Demat Account for NRI?",
        ],
    },
    {
        title: "INTRADAY TRADING",
        links: [
            "How to Calculate Stop Loss in\nIntraday Trading?",
            "How to Calculate and Check Turnover for Equity Delivery and Intraday?",
            "What is Intraday Trading?",
        ],
    },
    {
        title: "IPO",
        links: [
            "What is the IPO Listing Time in India?",
            "Types of Investors in an IPO",
            "How to Apply for IPO in HNI Category?",
        ],
    },
    {
        title: "F&O",
        links: [
            "What are Futures and Options?",
            "Difference Between Futures and\nOptions Explained.",
            "What Is the Difference Between Call and\nPut Option?",
        ],
    },
    {
        title: "SHARE MARKET",
        links: [
            "How to Invest in the Stock Market?",
            "What is Upper Circuit and Lower Circuit in\nStock Market?",
            "Types of Trading in the Stock Market",
        ],
    },
] as const;

// const MORE_LINKS = ["Become a Partner", "Pricing", "Refer"] as const;

export type SeoTab = (typeof SEO_TABS)[number];

type SeoTabContentProps = {
    tab: SeoTab;
};

export function SeoTabContent({ tab }: SeoTabContentProps) {
    const [showAllTopStocks, setShowAllTopStocks] = useState(false);

        if (tab === "Share Market") {
            return (
                <div className="flex flex-col gap-4 md:flex-row md:gap-s9">
                    <div className="flex w-full flex-col gap-4 rounded-[var(--radius-m)] border border-nav-stroke bg-nav-fg p-4 md:w-auto md:flex-row md:gap-s9">
                        <p className="text-sm font-semibold leading-5 text-white md:hidden">
                            SHARE MARKET TODAY
                        </p>
                        <div className="flex gap-s9 md:contents">
                            <div className="flex min-w-0 flex-1 flex-col gap-s9">
                                <p className="hidden text-base font-semibold leading-[22px] text-white md:block">
                                    SHARE MARKET TODAY
                                </p>
                                <div className="flex flex-col gap-s9">
                                    {SHARE_MARKET_TODAY_LINKS.slice(0, 4).map((label) => (
                                        <span
                                            key={label}
                                            className="w-full text-sm font-regular leading-5 text-neutralOpacityLight-50 md:w-[196px] md:text-base md:leading-[22px]"
                                        >
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="flex min-w-0 flex-1 flex-col gap-s9 md:w-[196px] md:flex-none md:pt-[46px]">
                                {SHARE_MARKET_TODAY_LINKS.slice(4).map((label) => (
                                    <span
                                        key={label}
                                        className="w-full text-sm font-regular leading-5 text-neutralOpacityLight-50 md:w-[196px] md:text-base md:leading-[22px]"
                                    >
                                        {label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="flex w-full flex-col gap-4 self-stretch rounded-[var(--radius-m)] border border-nav-stroke bg-nav-fg p-4 md:w-auto md:flex-row md:gap-s9">
                        <p className="text-sm font-semibold leading-5 text-white md:hidden">
                            ETFs
                        </p>
                        <div className="flex gap-s9 md:contents">
                            <div className="flex min-w-0 flex-1 flex-col gap-s9">
                                <p className="hidden text-base font-semibold leading-[22px] text-white md:block">
                                    ETFs
                                </p>
                                <div className="flex flex-col gap-s9">
                                    {ETF_LINKS.slice(0, 3).map((label) => (
                                        <span
                                            key={label}
                                            className="w-full text-sm font-regular leading-5 text-neutralOpacityLight-50 md:w-[196px] md:text-base md:leading-[22px]"
                                        >
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="flex min-w-0 flex-1 flex-col gap-s9 md:w-[196px] md:flex-none md:pt-[46px]">
                                {ETF_LINKS.slice(3).map((label) => (
                                    <span
                                        key={label}
                                        className="w-full text-sm font-regular leading-5 text-neutralOpacityLight-50 md:w-[196px] md:text-base md:leading-[22px]"
                                    >
                                        {label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (tab === "F&O" as FooterSeoTab) {
            return (
                <div className="flex flex-col gap-4 md:flex-row md:gap-s9">
                    <div className="flex w-full flex-col gap-4 rounded-[var(--radius-m)] border border-nav-stroke bg-nav-fg p-4 md:w-auto md:flex-row md:items-center md:gap-s9">
                        <p className="text-sm font-semibold leading-5 text-white md:hidden">
                            FUTURES
                        </p>
                        <div className="flex gap-s9 md:contents">
                            <div className="flex min-w-0 flex-1 flex-col gap-s9">
                                <p className="hidden text-base font-semibold leading-[22px] text-white md:block">
                                    FUTURES
                                </p>
                                <div className="flex flex-col gap-s9">
                                    {FUTURES_LINKS.slice(0, 6).map((label, index) => (
                                        <span
                                            key={`futures-l-${index}`}
                                            className="w-full text-sm font-regular leading-5 text-neutralOpacityLight-50 md:w-[196px] md:text-base md:leading-[22px]"
                                        >
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="flex min-w-0 flex-1 flex-col gap-s9 md:w-[196px] md:flex-none md:pt-[46px]">
                                {FUTURES_LINKS.slice(6).map((label, index) => (
                                    <span
                                        key={`futures-r-${index}`}
                                        className="w-full text-sm font-regular leading-5 text-neutralOpacityLight-50 md:w-[196px] md:text-base md:leading-[22px]"
                                    >
                                        {label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="flex w-full flex-col gap-4 rounded-[var(--radius-m)] border border-nav-stroke bg-nav-fg p-4 md:w-auto md:flex-row md:items-start md:gap-s9">
                        <p className="text-sm font-semibold leading-5 text-white md:hidden">
                            OPTIONS
                        </p>
                        <div className="flex gap-s9 md:contents">
                            <div className="flex min-w-0 flex-1 flex-col gap-s9">
                                <p className="hidden text-base font-semibold leading-[22px] text-white md:block">
                                    OPTIONS
                                </p>
                                <div className="flex flex-col gap-s9">
                                    {OPTIONS_LINKS.slice(0, 6).map((label, index) => (
                                        <span
                                            key={`options-l-${index}`}
                                            className="w-full text-sm font-regular leading-5 text-neutralOpacityLight-50 md:w-[196px] md:text-base md:leading-[22px]"
                                        >
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="flex min-w-0 flex-1 flex-col gap-s9 md:w-[196px] md:flex-none md:pt-[46px]">
                                {OPTIONS_LINKS.slice(6).map((label, index) => (
                                    <span
                                        key={`options-r-${index}`}
                                        className="w-full text-sm font-regular leading-5 text-neutralOpacityLight-50 md:w-[196px] md:text-base md:leading-[22px]"
                                    >
                                        {label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (tab === "IPO" as FooterSeoTab) {
            return (
                <>
                    {/* Mobile — Figma 4834:88006 */}
                    <div className="flex w-full items-center rounded-[var(--radius-m)] border border-nav-stroke bg-nav-fg p-4 md:hidden">
                        <div className="flex w-full gap-6">
                            {IPO_MOBILE_COLUMNS.map((column, colIndex) => (
                                <div
                                    key={`ipo-mobile-col-${colIndex}`}
                                    className="flex min-w-0 flex-1 flex-col items-start justify-center gap-s9"
                                >
                                    {column.map((label) => (
                                        <span
                                            key={label}
                                            className="w-full text-sm font-regular leading-5 text-neutralOpacityLight-50"
                                        >
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Desktop — Figma 4834:87113 */}
                    <div className="hidden w-full flex-col items-start gap-s9 rounded-[var(--radius-m)] border border-nav-stroke bg-nav-fg p-4 md:flex md:flex-row md:justify-between">
                        {IPO_COLUMNS.map((column, colIndex) => (
                            <div
                                key={`ipo-col-${colIndex}`}
                                className="flex w-full shrink-0 flex-col gap-s9 md:w-[196px]"
                            >
                                {column.map((label) => (
                                    <span
                                        key={label}
                                        className="w-full text-base font-regular leading-[22px] text-neutralOpacityLight-50 md:w-[196px]"
                                    >
                                        {label}
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                </>
            );
        }

        if (tab === "Indicies") {
            const indianMid = Math.ceil(INDIAN_INDICES_LINKS.length / 2);
            const globalMid = Math.ceil(GLOBAL_INDICES_LINKS.length / 2);

            return (
                <div className="flex flex-col gap-4 md:flex-row md:gap-s9">
                    <div className="flex w-full flex-col gap-4 rounded-[var(--radius-m)] border border-nav-stroke bg-nav-fg p-4 md:w-auto md:flex-row md:items-start md:gap-s9">
                        <p className="text-sm font-semibold leading-5 text-white md:hidden">
                            INDIAN
                        </p>
                        <div className="flex gap-s9 md:contents">
                            <div className="flex min-w-0 flex-1 flex-col gap-s9">
                                <p className="hidden text-base font-semibold leading-[22px] text-white md:block">
                                    INDIAN
                                </p>
                                <div className="flex flex-col gap-s9">
                                    {INDIAN_INDICES_LINKS.slice(0, indianMid).map((label) => (
                                        <a
                                            key={label}
                                            href={getIndexPageUrl(label)}
                                            className="w-full text-sm font-regular leading-5 text-neutralOpacityLight-50 hover:text-nav-text md:w-[196px] md:text-base md:leading-[22px]"
                                        >
                                            {label}
                                        </a>
                                    ))}
                                </div>
                            </div>
                            {INDIAN_INDICES_LINKS.length > indianMid ? (
                                <div className="flex min-w-0 flex-1 flex-col gap-s9 md:w-[196px] md:flex-none md:pt-[46px]">
                                    {INDIAN_INDICES_LINKS.slice(indianMid).map((label) => (
                                        <a
                                            key={label}
                                            href={getIndexPageUrl(label)}
                                            className="w-full text-sm font-regular leading-5 text-neutralOpacityLight-50 hover:text-nav-text md:w-[196px] md:text-base md:leading-[22px]"
                                        >
                                            {label}
                                        </a>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex w-full flex-col gap-4 self-stretch rounded-[var(--radius-m)] border border-nav-stroke bg-nav-fg p-4 md:w-auto md:flex-row md:items-start md:gap-s9">
                        <p className="text-sm font-semibold leading-5 text-white md:hidden">
                            GLOBAL
                        </p>
                        <div className="flex gap-s9 md:contents">
                            <div className="flex min-w-0 flex-1 flex-col gap-s9">
                                <p className="hidden text-base font-semibold leading-[22px] text-white md:block">
                                    GLOBAL
                                </p>
                                <div className="flex flex-col gap-s9">
                                    {GLOBAL_INDICES_LINKS.slice(0, globalMid).map((label) => (
                                        <span
                                            key={label}
                                            className="w-full text-sm font-regular leading-5 text-neutralOpacityLight-50 md:w-[196px] md:text-base md:leading-[22px]"
                                        >
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            {GLOBAL_INDICES_LINKS.length > globalMid ? (
                                <div className="flex min-w-0 flex-1 flex-col gap-s9 md:w-[196px] md:flex-none md:pt-[46px]">
                                    {GLOBAL_INDICES_LINKS.slice(globalMid).map((label) => (
                                        <span
                                            key={label}
                                            className="w-full text-sm font-regular leading-5 text-neutralOpacityLight-50 md:w-[196px] md:text-base md:leading-[22px]"
                                        >
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            );
        }

        if (tab === "Top Stocks") {
            return (
                <div className="flex w-full flex-col items-center gap-s9 rounded-[var(--radius-m)] border border-nav-stroke bg-nav-fg p-4">
                    <div className="flex w-full flex-col items-start gap-s9 md:flex-row md:gap-[37px] max-md:pb-1">
                        {TOP_STOCKS_COLUMNS.map((column, colIndex) => {
                            const labels = showAllTopStocks
                                ? [...column, ...TOP_STOCKS_MORE_COLUMNS[colIndex]]
                                : [...column];

                            return (
                                <div
                                    key={`top-stocks-col-${colIndex}`}
                                    className="flex w-full shrink-0 flex-col gap-s9 md:w-[196px]"
                                >
                                    {labels.map((label, labelIndex) => (
                                        <a
                                            key={`${colIndex}-${labelIndex}-${label}`}
                                            href={getTopStockPageUrl(label)}
                                            className="w-full whitespace-pre-wrap text-sm font-regular leading-[22px] text-neutralOpacityLight-50 hover:text-nav-text md:w-[196px] md:text-base"
                                        >
                                            {label}
                                        </a>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                    <button
                        type="button"
                        aria-expanded={showAllTopStocks}
                        onClick={() => setShowAllTopStocks((prev) => !prev)}
                        className="inline-flex h-[26px] items-center justify-center gap-1.5 text-sm font-medium leading-5 text-cta-primary-text underline decoration-solid"
                    >
                        {showAllTopStocks ? "Show Less" : "Show More"}
                        <ChevronDown
                            className={cn(
                                "size-4 shrink-0 transition-transform duration-200",
                                showAllTopStocks && "rotate-180",
                            )}
                            aria-hidden
                        />
                    </button>
                </div>
            );
        }

        if (tab === "Global Stocks") {
            const globalBase = process.env.ICM_GLOBAL_URL ?? "";

            return (
                <div className="flex flex-col gap-s9 md:flex-row">
                    <div className="flex w-full flex-col gap-s9 rounded-[var(--radius-m)] border border-nav-stroke bg-nav-fg p-4 md:w-[448px]">
                        <p className="text-base font-semibold leading-[22px] text-white">
                            TOP US STOCKS
                        </p>
                        <div className="flex flex-col gap-s9">
                            {TOP_US_STOCKS_LINKS.map(({ label, path }) => (
                                <a
                                    key={label}
                                    href={`${globalBase}${path}`}
                                    className="text-sm font-regular leading-[22px] text-neutralOpacityLight-50 hover:text-nav-text md:text-base"
                                >
                                    {label}
                                </a>
                            ))}
                        </div>
                    </div>
                    <div className="flex w-full flex-col gap-s9 self-stretch rounded-[var(--radius-m)] border border-nav-stroke bg-nav-fg p-4 md:w-[448px]">
                        <p className="text-base font-semibold leading-[22px] text-white">
                            US ETFs
                        </p>
                        <div className="flex flex-col gap-s9">
                            {US_ETFS_LINKS.map(({ label, path }) => (
                                <a
                                    key={label}
                                    href={`${globalBase}${path}`}
                                    className="text-sm font-regular leading-[22px] text-neutralOpacityLight-50 hover:text-nav-text md:text-base"
                                >
                                    {label}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        if (tab === "Calculators") {
            return (
                <div className="flex w-full flex-col items-start gap-s9 rounded-[var(--radius-m)] border border-nav-stroke bg-nav-fg p-4 md:flex-row md:justify-between">
                    {CALCULATOR_COLUMNS.map((column, colIndex) => (
                        <div
                            key={`calc-col-${colIndex}`}
                            className="flex w-full shrink-0 flex-col gap-s9 md:w-[196px]"
                        >
                            {column.map((label) => {
                                const href = calculatorHref(label);
                                const className =
                                    "w-full text-sm font-regular leading-[22px] text-neutralOpacityLight-50 md:w-[196px] md:text-base";
                                if (href) {
                                    return (
                                        <a
                                            key={label}
                                            href={href}
                                            className={`${className} hover:text-nav-text`}
                                        >
                                            {label}
                                        </a>
                                    );
                                }
                                return (
                                    <span key={label} className={className}>
                                        {label}
                                    </span>
                                );
                            })}
                        </div>
                    ))}
                </div>
            );
        }

        if (tab === "Knowledge Centre") {
            return (
                <div className="flex w-full flex-col gap-s9">
                    <div className="flex w-full flex-col gap-s9 md:flex-row">
                        {KNOWLEDGE_CENTER_SECTIONS.slice(0, 3).map((section) => (
                            <div
                                key={section.title}
                                className="flex min-w-0 flex-1 flex-col gap-s9 self-stretch rounded-[var(--radius-m)] border border-nav-stroke bg-nav-fg p-4"
                            >
                                <p className="text-base font-semibold leading-[22px] text-white">
                                    {section.title}
                                </p>
                                <div className="flex flex-col gap-s9">
                                    {section.links.map((label) => {
                                        const href = knowledgeCentreHref(label);
                                        const className =
                                            "whitespace-pre-wrap text-sm font-regular leading-[22px] text-neutralOpacityLight-50 md:text-base";
                                        if (href) {
                                            return (
                                                <a
                                                    key={label}
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`${className} hover:text-nav-text`}
                                                >
                                                    {label}
                                                </a>
                                            );
                                        }
                                        return (
                                            <span key={label} className={className}>
                                                {label}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex w-full flex-col gap-s9 md:flex-row">
                        {KNOWLEDGE_CENTER_SECTIONS.slice(3).map((section) => (
                            <div
                                key={section.title}
                                className="flex w-full flex-col gap-s9 self-stretch rounded-[var(--radius-m)] border border-nav-stroke bg-nav-fg p-4 md:w-[370.67px]"
                            >
                                <p className="text-base font-semibold leading-[22px] text-white">
                                    {section.title}
                                </p>
                                <div className="flex flex-col gap-s9">
                                    {section.links.map((label) => {
                                        const href = knowledgeCentreHref(label);
                                        const className =
                                            "whitespace-pre-wrap text-sm font-regular leading-[22px] text-neutralOpacityLight-50 md:text-base";
                                        if (href) {
                                            return (
                                                <a
                                                    key={label}
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`${className} hover:text-nav-text`}
                                                >
                                                    {label}
                                                </a>
                                            );
                                        }
                                        return (
                                            <span key={label} className={className}>
                                                {label}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // if (tab === "Blogs") {
        //     return null;
        // }

        // if (tab === "More") {
        //     return (
        //         <div className="flex w-full flex-col items-start gap-s9 rounded-[var(--radius-m)] border border-nav-stroke bg-nav-fg p-4 md:flex-row md:gap-[50px]">
        //             {MORE_LINKS.map((label) => (
        //                 <span
        //                     key={label}
        //                     className="w-full shrink-0 text-sm font-regular leading-[22px] text-neutralOpacityLight-50 md:w-[196px] md:text-base"
        //                 >
        //                     {label}
        //                 </span>
        //             ))}
        //         </div>
        //     );
        // }

        return null;
}
